import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getGroups, createGroup, updateGroup, deleteGroup, getUsers, updateUser } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, UsersRound, Edit, Trash2, UserPlus, X } from 'lucide-react';

export default function Groups() {
  const { hasPermission } = useAuth();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [showMembers, setShowMembers] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, usersRes] = await Promise.all([getGroups(), getUsers()]);
      setGroups(groupsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      await createGroup(formData);
      toast.success('Grupo creado');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear grupo');
    }
  };

  const handleEdit = (group) => {
    setEditGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      members: group.members || []
    });
  };

  const handleUpdate = async () => {
    try {
      await updateGroup(editGroup.id, formData);
      toast.success('Grupo actualizado');
      setEditGroup(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar grupo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este grupo?')) return;
    try {
      await deleteGroup(id);
      toast.success('Grupo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar grupo');
    }
  };

  const toggleMember = (userId) => {
    const newMembers = formData.members.includes(userId)
      ? formData.members.filter(m => m !== userId)
      : [...formData.members, userId];
    setFormData({ ...formData, members: newMembers });
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', members: [] });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId) || {};
  };

  const getGroupMembers = (group) => {
    return users.filter(u => group.members?.includes(u.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="groups-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            Grupos
          </h1>
          <p className="text-muted-foreground">
            Organiza a los miembros en grupos para eventos y ensayos
          </p>
        </div>
        {hasPermission('grupos', 'crear') && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Grupo
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((group) => {
          const members = getGroupMembers(group);
          return (
            <Card key={group.id} className="card-hover">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <UsersRound className="w-5 h-5 text-emerald-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{group.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {members.length} miembros
                      </p>
                    </div>
                  </div>
                  {hasPermission('grupos', 'editar') && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(group)}
                        data-testid={`edit-group-${group.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(group.id)}
                        data-testid={`delete-group-${group.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {group.description && (
                  <p className="text-sm text-muted-foreground mb-4">{group.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {members.slice(0, 5).map((user) => (
                      <Avatar key={user.id} className="w-8 h-8 border-2 border-white">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                          {getInitials(user.full_name)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {members.length > 5 && (
                      <div className="w-8 h-8 rounded-full bg-stone-200 border-2 border-white flex items-center justify-center text-xs font-medium">
                        +{members.length - 5}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMembers(group)}
                    className="ml-auto"
                  >
                    Ver todos
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {groups.length === 0 && (
          <Card className="sm:col-span-2 lg:col-span-3">
            <CardContent className="py-12 text-center">
              <UsersRound className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="text-muted-foreground">No hay grupos creados</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showCreate || !!editGroup} onOpenChange={() => { setShowCreate(false); setEditGroup(null); resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editGroup ? 'Editar Grupo' : 'Nuevo Grupo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del grupo"
                data-testid="group-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción del grupo..."
                rows={2}
                data-testid="group-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Miembros ({formData.members.length} seleccionados)</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-3 p-2 hover:bg-stone-50 rounded">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={formData.members.includes(user.id)}
                      onCheckedChange={() => toggleMember(user.id)}
                    />
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.avatar_url} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
                        {getInitials(user.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <label htmlFor={`user-${user.id}`} className="text-sm cursor-pointer flex-1">
                      {user.full_name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditGroup(null); resetForm(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={editGroup ? handleUpdate : handleCreate}
              data-testid="submit-group-btn"
            >
              {editGroup ? 'Actualizar' : 'Crear'} Grupo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Members Dialog */}
      <Dialog open={!!showMembers} onOpenChange={() => setShowMembers(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Miembros de {showMembers?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {showMembers && getGroupMembers(showMembers).map((user) => (
                <div key={user.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <Avatar>
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {getInitials(user.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{user.full_name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              ))}
              {showMembers && getGroupMembers(showMembers).length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Este grupo no tiene miembros
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembers(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
